import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Button from '../Button/Button';
import { useRouter } from 'next/navigation';
import { ROUTES } from '@/config/routes';
import { T } from '@tolgee/react';
import LimitModal from '@/components/Modal/LimitModal';

interface MainExpertCardProps {
  imageUrl: string;
  name: string;
  title: string;
  description: string;
  tags: string[];
  experience: string;
  expertId: string;
  id: number;
  onOpenModal: () => void;
}

const MainExpertCard: React.FC<MainExpertCardProps> = ({
  imageUrl,
  name,
  title,
  description,
  tags,
  expertId,
  experience,
  id: _id,
  onOpenModal,
}) => {
  const [gradientDirection, setGradientDirection] = useState<string>('');
  const router = useRouter();

  const [usageLimitExceeded, setUsageLimitExceeded] = useState<boolean>(false);

  useEffect(() => {
    const newGradient = getRandomGradientDirection();
    setGradientDirection(newGradient);
  }, []);

  const handleNavigateToExpert = () => {
    if (expertId) {
      const fetchAvailabilities = async () => {
        try {
          void router.push(ROUTES.BOOK_EXPERT(expertId as string));
        } catch (error) {
          if ((error as { status?: number })?.status === 402) {
            setUsageLimitExceeded(true);
          }
          console.error('Error fetching availabilities:', error);
        }
      };
      void fetchAvailabilities();
    }
  };

  const closeLimitModal = (_e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setUsageLimitExceeded(false);
  };

  return (
    <>
      {usageLimitExceeded && <LimitModal isOpen={usageLimitExceeded} onClose={closeLimitModal} />}
      <div
        className="flex bg-gray rounded-2xl overflow-hidden mb-8 last:mb-0 relative group cursor-pointer"
        onClick={onOpenModal}
      >
        <div className={`flex items-center rounded-2xl relative md:p-20 ${gradientDirection}`}>
          <Image
            src={imageUrl}
            alt={name}
            width={208}
            height={208}
            className="rounded-tl-[12px] rounded-tr-[12px] rounded-br-[50px] rounded-bl-[12px] object-cover h-52 w-52"
          />
        </div>
        <div className="w-3/4 p-12">
          {/*<div className="flex justify-between">*/}
          {/*  <p className="text-primary uppercase mb-2 text-sm">{title}</p>*/}
          {/*  <p className="text-base mb-4 text-right opacity-40">{experience}</p>*/}
          {/*</div>*/}

          <div className="flex flex-col md:flex-row justify-between">
            <p className="text-primary uppercase mb-2 text-sm">{title}</p>
            <p className="text-lg mb-4 md:mb-0 text-left md:text-right opacity-40">{experience}</p>
          </div>
          <h3 className="text-lg md:text-4xl mb-2">{name}</h3>
          <p className="text-lg text-black mb-4 line-clamp-4 font-poppins">{description}</p>
          <div className="flex flex-col md:flex-row justify-between items-center mt-8">
            <div className="flex flex-wrap mb-4 md:mb-0">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-white text-black p-4 py-2.5 rounded-full text-lg mr-2 mb-2"
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="mt-4 md:mt-0 md:relative">
              <div className="md:opacity-0 md:translate-y-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNavigateToExpert();
                  }}
                >
                  <T>expert_details.button.book_now.label</T>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

function getRandomGradientDirection() {
  const gradientDirections = [
    'bg-gradient-to-t from-main to-black',
    'bg-gradient-to-r from-main to-black',
    'bg-gradient-to-l from-main to-black',
    'bg-gradient-to-br from-main to-black',
    'bg-gradient-to-bl from-main to-black',
    'bg-gradient-to-tr from-main to-black',
    'bg-gradient-to-tl from-main to-black',
  ];

  return gradientDirections[Math.floor(Math.random() * gradientDirections.length)];
}

export default MainExpertCard;
